/**
 * useMessages Hook
 *
 * Custom hook for managing messaging state and API calls.
 * Handles conversations, messages, sending, and real-time updates.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-476 - Core Messaging Infrastructure (13 SP)
 * Location: apps/web/src/hooks/useMessages.js
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiRequest from '../services/api';
import { io } from 'socket.io-client';

/**
 * useMessages Hook
 *
 * @returns {Object} Messaging state and functions
 */
export const useMessages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const [conversationsError, setConversationsError] = useState(null);
  const [messagesError, setMessagesError] = useState(null);

  const socketRef = useRef(null);
  const selectedConversationIdRef = useRef(selectedConversationId);
  const [connected, setConnected] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  // WebSocket connection setup
  useEffect(() => {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      return;
    }

    // Connect to Socket.io server
    const socket = io(import.meta.env.VITE_API_URL || 'http://localhost:3030', {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', (_error) => {
      setConnected(false);
    });

    // Message events
    socket.on('message:new', ({ message, conversation: _conversation }) => {
      const currentConvId = selectedConversationIdRef.current;
      // Add message to current thread if viewing this conversation
      if (message.conversation_id === currentConvId) {
        setMessages((prev) => [...prev, message]);
      }

      // Update conversation list preview
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === message.conversation_id
            ? {
                ...conv,
                last_message_preview: message.message_text.substring(0, 200),
                last_message_at: message.created_at,
                unread_count: conv.id === currentConvId ? conv.unread_count : conv.unread_count + 1,
              }
            : conv
        )
      );

      // Update unread count if not viewing this conversation
      if (message.conversation_id !== currentConvId) {
        setUnreadCount((prev) => prev + 1);
      }
    });

    socket.on('message:read', ({ messageId, conversationId, readAt }) => {
      // Update message read status in current thread
      if (conversationId === selectedConversationIdRef.current) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, is_read: true, read_at: readAt }
              : msg
          )
        );
      }
    });

    socket.on('message:delivered', ({ messageId: _messageId, conversationId: _conversationId }) => {
      // Message delivered event handler
    });

    socket.on('message:typing', ({ conversationId: _conversationId, userId: _userId, isTyping: _isTyping }) => {
      // Could add typing indicator state here if needed
    });

    socket.on('message:error', ({ message: _message, code: _code }) => {
      // WebSocket error handler
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []); // Empty deps - connect once on mount, uses ref for selectedConversationId

  // Subscribe to conversation when selected
  useEffect(() => {
    if (socketRef.current && selectedConversationId) {
      socketRef.current.emit('message:subscribe', { conversationId: selectedConversationId });

      return () => {
        socketRef.current.emit('message:unsubscribe', { conversationId: selectedConversationId });
      };
    }
  }, [selectedConversationId]);

  // Fetch conversations
  const fetchConversations = useCallback(async (params = {}) => {
    try {
      setLoadingConversations(true);
      setConversationsError(null);

      const queryParams = new URLSearchParams(params);
      const queryString = queryParams.toString();
      const url = `/api/v1/messages/conversations${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest(url, { method: 'GET' });

      if (response.success) {
        setConversations(response.data.conversations);
        setUnreadCount(response.data.total_unread || 0);
      }
    } catch (error) {
      if (error.status === 401) {
        setConversationsError('Session expired. Please log in again.');
      } else {
        setConversationsError(error.message || 'Failed to load conversations');
      }
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // Mark conversation as read
  const markAsRead = useCallback(async (conversationId) => {
    try {
      await apiRequest(`/api/v1/messages/conversations/${conversationId}/read`, {
        method: 'PUT',
      });

      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );

      // Recalculate total unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      // Silently fail - not critical if mark as read fails
    }
  }, []);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId, params = {}) => {
    try {
      setLoadingMessages(true);
      setMessagesError(null);

      const queryParams = new URLSearchParams(params);
      const queryString = queryParams.toString();
      const url = `/api/v1/messages/conversations/${conversationId}${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest(url, { method: 'GET' });

      if (response.success) {
        setMessages(response.data.messages);
        // Auto-mark as read when viewing
        markAsRead(conversationId);
      }
    } catch (error) {
      if (error.status === 401) {
        setMessagesError('Session expired. Please log in again.');
      } else {
        setMessagesError(error.message || 'Failed to load messages');
      }
    } finally {
      setLoadingMessages(false);
    }
  }, [markAsRead]);

  // Send message
  const sendMessage = useCallback(async (conversationId, recipientId, messageText) => {
    try {
      setSending(true);

      const response = await apiRequest(`/api/v1/messages/conversations/${conversationId}/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipient_id: recipientId,
          message_text: messageText,
        }),
      });

      if (response.success) {
        const newMessage = response.data.message;

        // Add message to current thread
        setMessages((prev) => [...prev, newMessage]);

        // Update conversation list preview
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  last_message_preview: messageText.substring(0, 200),
                  last_message_at: newMessage.created_at,
                }
              : conv
          )
        );

        return newMessage;
      }
    } catch (error) {
      if (error.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  }, []);

  // Select conversation
  const selectConversation = useCallback((conversationId) => {
    setSelectedConversationId(conversationId);
    if (conversationId) {
      fetchMessages(conversationId);
    } else {
      setMessages([]);
    }
  }, [fetchMessages]);

  // Archive conversation
  const archiveConversation = useCallback(async (conversationId) => {
    try {
      await apiRequest(`/api/v1/messages/conversations/${conversationId}/archive`, {
        method: 'PUT',
      });

      // Remove from list
      setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));

      // If this was selected, clear selection
      if (conversationId === selectedConversationId) {
        setSelectedConversationId(null);
        setMessages([]);
      }
    } catch (error) {
      if (error.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(error.message || 'Failed to archive conversation');
    }
  }, [selectedConversationId]);

  // Search messages in conversation
  const searchMessages = useCallback(async (conversationId, searchText) => {
    try {
      const response = await apiRequest(`/api/v1/messages/conversations/${conversationId}/search`, {
        method: 'POST',
        body: JSON.stringify({
          search_text: searchText,
        }),
      });

      if (response.success) {
        return response.data;
      }
    } catch (error) {
      if (error.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(error.message || 'Failed to search messages');
    }
  }, []);

  // Get unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiRequest('/api/v1/messages/unread-count', { method: 'GET' });

      if (response.success) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      // Silently fail - not critical if unread count fetch fails
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, [fetchConversations, fetchUnreadCount]);

  return {
    // State
    conversations,
    selectedConversationId,
    messages,
    unreadCount,
    loadingConversations,
    loadingMessages,
    sending,
    conversationsError,
    messagesError,
    connected, // WebSocket connection status

    // Functions
    fetchConversations,
    fetchMessages,
    sendMessage,
    selectConversation,
    markAsRead,
    archiveConversation,
    searchMessages,
    fetchUnreadCount,
  };
};

export default useMessages;
